'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@elmnts/ui/button'
import { channelsKeys } from '@/hooks/useChannelsQuery'
import { useChatBot } from '@/providers/ChatBotContext'
import { BrandSignature } from '@/components/BrandSignature'
import { ChannelCard } from './_components/ChannelCard'
import { ChannelHomeSkeleton } from './_components/ChannelHomeSkeleton'
import { WelcomeHeader } from './_components/WelcomeHeader'

export default function Chatbot() {
  const { channels, isLoadingFreshData } = useChatBot()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Canal único não tem o que escolher: uma tela de seleção com um card só
  // cobraria um clique a mais em todo login.
  useEffect(() => {
    if (channels.length === 1) {
      setIsRedirecting(true)
      router.push('/chatbot/' + channels[0].db_id)
    }
  }, [channels, router])

  if (isRedirecting || (channels.length === 0 && isLoadingFreshData)) {
    return (
      <div className="w-full px-8 py-10">
        <ChannelHomeSkeleton />
      </div>
    )
  }

  if (channels.length === 0) {
    return (
      <div className="flex w-full flex-col items-center gap-3 px-8 py-20 text-center">
        <h1 className="text-xl font-semibold text-foreground">Nenhum canal disponível</h1>
        <p className="text-sm text-muted-foreground">
          Não encontramos canais vinculados à sua conta.
        </p>
        {/* Recarregar a página inteira aqui era destrutivo e redundante: a query
            de canais já refaz sozinha a cada 30s. Basta um pedido explícito. */}
        <Button
          variant="outline"
          className="mt-2"
          onClick={() => queryClient.invalidateQueries({ queryKey: channelsKeys.all })}
        >
          Verificar novamente
        </Button>
      </div>
    )
  }

  // Cópia antes de ordenar: `channels` é o array do provider e `sort` é in-place.
  // Ordem alfabética estável de propósito — ordenar por não lidas faria os cards
  // trocarem de lugar sozinhos conforme chegam mensagens, e o destaque visual do
  // card já resolve a urgência sem custar a memória de posição.
  const sorted = [...channels].sort((a, b) => a.name.localeCompare(b.name))
  const unreadTotal = channels.reduce((sum, c) => sum + c.total_unread_messages, 0)

  return (
    <div className="relative flex-1 overflow-y-auto">
      {/* Único elemento decorativo da tela: um halo da cor da marca ancorado no
          alto, atrás do cabeçalho. O fundo chapado fazia a área abaixo dos cards
          parecer um buraco; o halo dá origem à composição sem competir com dado
          nenhum. Fora daqui, nada de gradiente. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(55rem_30rem_at_10%_-8%,rgba(237,20,91,0.10),transparent_65%)]"
      />

      {/* min-h-full + mt-auto no rodapé: a página ganha chão. Antes o conteúdo
          terminava no meio da tela e o resto era vazio sem função. */}
      <div className="relative mx-auto flex min-h-full w-full flex-col px-8 py-10">
        <WelcomeHeader
          channelCount={channels.length}
          unreadTotal={unreadTotal}
          now={new Date()}
        />

        {/* Largura contida e colunas escalonadas: espalhar os cards de ponta a
            ponta os deixava finos e a linha única deixava tudo abaixo vazio. */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sorted.map((channel) => (
            <ChannelCard
              key={channel.db_id}
              channel={channel}
              onSelect={(dbId) => router.push('/chatbot/' + dbId)}
            />
          ))}
        </div>

        {/* mt-auto dá chão à página: sem isso o conteúdo termina no meio da
            tela e o resto é vazio sem função. */}
        <BrandSignature className="mt-auto pt-10 pb-6" />
      </div>
    </div>
  )
}
